import '@qapline/translations';
import 'styles/_global.scss';

import { lazy, Suspense } from 'react';
import { I18nProvider } from 'react-aria';
import { MapProvider } from 'react-map-gl/mapbox';
import { Provider } from 'react-redux';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router-dom';
import { Authenticated } from '@qapline/api';
import { Toast } from '@qapline/ui';
import * as SentryReact from '@sentry/react';
import { config } from 'config';
import { SelectedDispatchersProvider } from 'modules/commissions/hooks/useSelectedDispatchers';
import { AuthenticatedLayout } from 'modules/layout';
import { Spinner } from 'modules/ui';
import { store } from 'store';

import { AdminRoute } from './AdminRoute';

//const ErrorPage = lazy(() => import('@qapline/ui/error/ErrorPage').then((m) => ({ default: m.ErrorPage })));
const ErrorPage = () => <></>;
const LogoutPage = lazy(() =>
    import('@qapline/api/auth/components/LogoutPage').then((m) => ({ default: m.LogoutPage })),
);
const LoginForm = lazy(() => import('@qapline/api/auth/components/LoginForm').then((m) => ({ default: m.LoginForm })));
const CommissionsPage = lazy(() =>
    import('modules/commissions/components/CommissionsPage').then((m) => ({ default: m.CommissionsPage })),
);
const CommissionsDetailPage = lazy(() =>
    import('modules/commissions').then((m) => ({ default: m.CommissionsDetailPage })),
);
const CommissionsNewPage = lazy(() => import('modules/commissions').then((m) => ({ default: m.CommissionsNewPage })));
const CommissionsExport = lazy(() => import('modules/commissions').then((m) => ({ default: m.CommissionsExport })));
const CarriersPage = lazy(() => import('modules/carriers').then((m) => ({ default: m.CarriersPage })));
const CarrierDetailPage = lazy(() => import('modules/carriers').then((m) => ({ default: m.CarrierDetailPage })));
const CarriersNewPage = lazy(() => import('modules/carriers').then((m) => ({ default: m.CarriersNewPage })));
const CustomersPage = lazy(() => import('modules/customers').then((m) => ({ default: m.CustomersPage })));
const CustomersDetailPage = lazy(() =>
    import('modules/customers/components/CustomersDetailPage').then((m) => ({ default: m.CustomersDetailPage })),
);
const CustomersNewPage = lazy(() =>
    import('modules/customers/components/CustomersNewPage').then((m) => ({ default: m.CustomersNewPage })),
);
const InvoicingPage = lazy(() => import('modules/invoicing').then((m) => ({ default: m.InvoicingPage })));
const InvoicingDetailPage = lazy(() => import('modules/invoicing').then((m) => ({ default: m.InvoicingDetailPage })));
const InvoicingNewPage = lazy(() =>
    import('modules/invoicing/components/InvoicingNewPage').then((m) => ({ default: m.InvoicingNewPage })),
);
const OffersPage = lazy(() => import('modules/offers').then((m) => ({ default: m.OffersPage })));
const OffersDetailPage = lazy(() => import('modules/offers').then((m) => ({ default: m.OffersDetailPage })));
const DispatcherDetailPage = lazy(() =>
    import('modules/onboarding').then((m) => ({ default: m.DispatcherDetailPage })),
);
const DispatcherNewPage = lazy(() => import('modules/onboarding').then((m) => ({ default: m.DispatcherNewPage })));
const OnboardingPage = lazy(() => import('modules/onboarding').then((m) => ({ default: m.OnboardingPage })));
const Profile = lazy(() => import('modules/profile').then((m) => ({ default: m.Profile })));
const CreateAttachmentPage = lazy(() =>
    import('modules/attachments/components/CreateAttachmentPage').then((m) => ({ default: m.CreateAttachmentPage })),
);
const EditAttachmentPage = lazy(() =>
    import('modules/attachments/components/EditAttachmentPage').then((m) => ({ default: m.EditAttachmentPage })),
);
const AttachmentsUploadPage = lazy(() =>
    import('modules/attachments/components/upload/AttachmentsUploadPage').then((m) => ({
        default: m.AttachmentsUploadPage,
    })),
);
const DashboardPage = lazy(() =>
    import('modules/dashboard/components/DashboardPage').then((m) => ({ default: m.DashboardPage })),
);
const ExpensesOverviewPage = lazy(() => import('modules/expenses').then((m) => ({ default: m.ExpensesOverviewPage })));
const ExpenseCreatePage = lazy(() => import('modules/expenses').then((m) => ({ default: m.ExpenseCreatePage })));
const ExpenseEditPage = lazy(() => import('modules/expenses').then((m) => ({ default: m.ExpenseEditPage })));

const CashflowPage = lazy(() => import('modules/cashflow').then((m) => ({ default: m.CashflowPage })));
const RedirectKeepingSearch = ({ to }: { to: string }) => {
    const { search, hash } = useLocation();

    return <Navigate to={{ pathname: to, search, hash }} replace />;
};

const sentryCreateBrowserRouter = SentryReact.wrapCreateBrowserRouterV6(createBrowserRouter);
const router = sentryCreateBrowserRouter([
    {
        path: '/',
        element: (
            <Authenticated allowedRoles={config.allowedRoles}>
                <AuthenticatedLayout />
            </Authenticated>
        ),
        errorElement: <ErrorPage />,
        children: [
            {
                index: true,
                element: <Navigate to={config.routes.commission.overview} replace />,
            },
            {
                path: config.routes.auth.logout,
                element: <LogoutPage />,
            },
            {
                path: config.routes.commission.overview,
                element: <CommissionsPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.commission.detail,
                element: (
                    <SelectedDispatchersProvider>
                        <CommissionsDetailPage />
                    </SelectedDispatchersProvider>
                ),
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.commission.create,
                element: (
                    <SelectedDispatchersProvider>
                        <CommissionsNewPage />
                    </SelectedDispatchersProvider>
                ),
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.commission.export,
                element: <CommissionsExport />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.carrier.overview,
                element: <CarriersPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.carrier.detail,
                element: <CarrierDetailPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.carrier.create,
                element: <CarriersNewPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.customer.overview,
                element: <CustomersPage />,
            },
            { path: config.routes.customer.detail, element: <CustomersDetailPage />, errorElement: <ErrorPage /> },
            { path: config.routes.customer.create, element: <CustomersNewPage />, errorElement: <ErrorPage /> },
            {
                path: config.routes.invoice.overview,
                element: <InvoicingPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.invoice.detail,
                element: <InvoicingDetailPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.invoice.create,
                element: <InvoicingNewPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.offer.overview,
                element: <OffersPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.offer.detail,
                element: (
                    <SelectedDispatchersProvider>
                        <MapProvider>
                            <OffersDetailPage />
                        </MapProvider>
                    </SelectedDispatchersProvider>
                ),
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.dispatcher.detail,
                element: <DispatcherDetailPage />,
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.dispatcher.create,
                element: <DispatcherNewPage />,
                errorElement: <ErrorPage />,
            },
            // {
            //     path: config.routes.profile,
            //     element: <Profile />,
            //     errorElement: <ErrorPage />,
            // },
            {
                path: config.routes.attachment.detail,
                element: <EditAttachmentPage />,
            },
            {
                path: config.routes.attachment.create,
                element: <CreateAttachmentPage />,
            },
            {
                path: config.routes.dashboard.dashboard,
                element: <DashboardPage />,
            },
            {
                path: config.routes.dashboard.cashflow,
                element: (
                    <AdminRoute>
                        <CashflowPage />
                    </AdminRoute>
                ),
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.expense.overview,
                element: (
                    <AdminRoute>
                        <ExpensesOverviewPage />
                    </AdminRoute>
                ),
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.expense.create,
                element: (
                    <AdminRoute>
                        <ExpenseCreatePage />
                    </AdminRoute>
                ),
                errorElement: <ErrorPage />,
            },
            {
                path: config.routes.expense.detail,
                element: (
                    <AdminRoute>
                        <ExpenseEditPage />
                    </AdminRoute>
                ),
                errorElement: <ErrorPage />,
            },
        ],
    },
    //{ path: config.routes.dispatcher.onboarding, element: <OnboardingPage />, errorElement: <ErrorPage /> },
    {
        path: config.routes.attachment.upload,
        element: <AttachmentsUploadPage />,
        errorElement: <ErrorPage />,
    },
    {
        path: config.legacyRoutes.attachment.upload,
        element: <RedirectKeepingSearch to={config.routes.attachment.upload} />,
        errorElement: <ErrorPage />,
    },
    {
        path: config.routes.auth.login,
        element: <LoginForm allowedRoles={config.allowedRoles} />,
        errorElement: <ErrorPage />,
    },
]);

export const Application = () => (
    <SentryReact.ErrorBoundary>
        <Provider store={store}>
            <I18nProvider locale="cs-CZ">
                <Suspense fallback={<Spinner fullScreen />}>
                    <Toast />
                    <RouterProvider router={router} />
                </Suspense>
            </I18nProvider>
        </Provider>
    </SentryReact.ErrorBoundary>
);
